import React, { useState, Component } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage, getSlugForLanguage } from '../contexts/LanguageContext';
// Indonesia Flag SVG Component
function IndonesiaFlag({
  className = 'w-6 h-4'
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" data-id="element-151">
      <rect width="900" height="600" fill="#fff" data-id="element-152" />
      <rect width="900" height="300" fill="#FF0000" data-id="element-153" />
      <rect y="300" width="900" height="300" fill="#FFFFFF" data-id="element-154" />
    </svg>;
}
// UK Flag SVG Component
function UKFlag({
  className = 'w-6 h-4'
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" data-id="element-155">
      <clipPath id="s" data-id="element-156">
        <path d="M0,0 v30 h60 v-30 z" data-id="element-157" />
      </clipPath>
      <clipPath id="t" data-id="element-158">
        <path d="M30,15 h30 v15 z v-15 h-30 z h-30 v15 z v-15 h30 z" data-id="element-159" />
      </clipPath>
      <g clipPath="url(#s)" data-id="element-160">
        <path d="M0,0 v30 h60 v-30 z" fill="#012169" data-id="element-161" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" data-id="element-162" />
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" data-id="element-163" />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" data-id="element-164" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" data-id="element-165" />
      </g>
    </svg>;
}
export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    language,
    setLanguage,
    t,
    getSlug
  } = useLanguage();
  // Get current path without language prefix and slug
  const getCurrentBasePath = () => {
    const path = location.pathname;
    const pathWithoutLang = path.replace(/^\/(id|en)/, '');
    // Extract the base path (e.g., /tentang-kami or /about-us)
    return pathWithoutLang || '/';
  };
  const navigation = [{
    name: t('Tentang Kami', 'About'),
    slug: 'tentang-kami'
  }, {
    name: t('Manajemen', 'Management'),
    slug: 'manajemen'
  }, {
    name: t('Proyek', 'Projects'),
    slug: 'proyek'
  }, {
    name: t('Produk & Layanan', 'Products & Services'),
    slug: 'produk-layanan'
  }, {
    name: t('Kontak', 'Contact'),
    slug: 'kontak'
  }];
  const isActive = (slug: string) => {
    const currentPath = getCurrentBasePath();
    const slugInCurrentLang = getSlug(slug);
    return currentPath === `/${slugInCurrentLang}`;
  };
  const toggleLanguage = () => {
    const newLang = language === 'id' ? 'en' : 'id';
    const currentPath = getCurrentBasePath();
    // Get the canonical slug from current path
    const currentSlug = currentPath.replace('/', '');
    // Convert slug to new language
    const newSlug = currentSlug ? getSlugForLanguage(currentSlug, newLang) : '';
    setLanguage(newLang);
    // Navigate to the same page but with new language and slug
    navigate(`/${newLang}${newSlug ? `/${newSlug}` : ''}`);
  };
  const getLanguageLink = (slug: string) => {
    const localizedSlug = getSlug(slug);
    return `/${language}/${localizedSlug}`;
  };
  return <header className="fixed w-full bg-white shadow-md z-50" data-id="element-166">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-167">
        <div className="flex justify-between items-center h-16 sm:h-20" data-id="element-168">
          {/* Logo */}
          <Link to={`/${language}`} className="flex-shrink-0" data-id="element-169">
            <img src="https://cdn.magicpatterns.com/uploads/sSNAfpvwR9DMLUnYLkRqa2/logo-GRG-Graha-Riau-Gemilang.png" alt="PT Graha Riau Gemilang" className="h-10 sm:h-12 w-auto" data-id="element-170" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8" data-id="element-171">
            {navigation.map(item => <Link key={item.slug} to={getLanguageLink(item.slug)} className={`text-sm xl:text-base font-medium transition-colors whitespace-nowrap ${isActive(item.slug) ? 'text-[#C4161C]' : 'text-gray-700 hover:text-[#C4161C]'}`} data-id="element-172">
                {item.name}
              </Link>)}

            {/* Language Toggle with Flags */}
            <button onClick={toggleLanguage} className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-200 hover:border-[#C4161C] hover:bg-red-50 transition-all group" aria-label="Toggle language" title={language === 'id' ? 'Switch to English' : 'Ganti ke Bahasa Indonesia'} data-id="element-173">
              {language === 'id' ? <>
                  <IndonesiaFlag className="w-6 h-4 rounded-sm shadow-sm" data-id="element-174" />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-[#C4161C]" data-id="element-175">
                    ID
                  </span>
                </> : <>
                  <UKFlag className="w-6 h-4 rounded-sm shadow-sm" data-id="element-176" />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-[#C4161C]" data-id="element-177">
                    EN
                  </span>
                </>}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center space-x-3" data-id="element-178">
            {/* Mobile Language Toggle with Flags */}
            <button onClick={toggleLanguage} className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-[#C4161C] hover:bg-red-50 transition-all" aria-label="Toggle language" data-id="element-179">
              {language === 'id' ? <>
                  <IndonesiaFlag className="w-5 h-3.5 rounded-sm shadow-sm" data-id="element-180" />
                  <span className="text-xs font-medium text-gray-700" data-id="element-181">ID</span>
                </> : <>
                  <UKFlag className="w-5 h-3.5 rounded-sm shadow-sm" data-id="element-182" />
                  <span className="text-xs font-medium text-gray-700" data-id="element-183">EN</span>
                </>}
            </button>

            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-700 hover:text-[#C4161C] transition-colors p-2" aria-label="Toggle menu" data-id="element-184">
              {isOpen ? <X className="h-6 w-6" data-id="element-185" /> : <Menu className="h-6 w-6" data-id="element-186" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && <div className="lg:hidden pb-4 border-t border-gray-100" data-id="element-187">
            <div className="flex flex-col space-y-1 pt-2" data-id="element-188">
              {navigation.map(item => <Link key={item.slug} to={getLanguageLink(item.slug)} onClick={() => setIsOpen(false)} className={`px-3 py-3 text-base font-medium transition-colors rounded-lg ${isActive(item.slug) ? 'text-[#C4161C] bg-red-50' : 'text-gray-700 hover:text-[#C4161C] hover:bg-gray-50'}`} data-id="element-189">
                  {item.name}
                </Link>)}
            </div>
          </div>}
      </nav>
    </header>;
}